import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import {
  Translation,
  TranslocoConfig,
  TranslocoLoader,
  TranslocoMissingHandler,
  TranslocoService
} from '@jsverse/transloco';
import { catchError, forkJoin, map, of } from 'rxjs';

export const TENANT_ID_TOKEN = new InjectionToken<() => number | undefined>('TENANT_ID_TOKEN');

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly getTenantId = inject(TENANT_ID_TOKEN, { optional: true });

  getTranslation(lang: string) {
    // example for lang:
    // de.json
    // <scope>/de.json
    const langPath = lang.split('/');
    if (langPath.length === 1) {
      const tenantId = this.getTenantId?.();
      const sources$ = [
        this.http
          .get<Translation>(`/i18n/common/${lang}.json?v=${Date.now()}`)
          .pipe(catchError(() => of({} as Translation))),
        this.http
          .get<Translation>(`/i18n/${lang}.json?v=${Date.now()}`)
          .pipe(catchError(() => of({} as Translation)))
      ];
      if (tenantId != null) {
        sources$.push(
          this.http
            .get<Translation>(`/i18n/${tenantId}/${lang}.json?v=${Date.now()}`)
            .pipe(catchError(() => of({} as Translation)))
        );
      }
      return forkJoin(sources$).pipe(
        map(([common, project, tenantOverride = {}]) =>
          this.deepMerge({}, common, project, tenantOverride)
        )
      );
    }
    return this.http.get<Translation>(`/i18n/${lang}.json?v=${Date.now()}`);
  }

  private deepMerge(
    target: Record<string, any>,
    ...sources: Record<string, any>[]
  ): Record<string, any> {
    for (const source of sources) {
      for (const key of Object.keys(source)) {
        if (
          source[key] !== null &&
          typeof source[key] === 'object' &&
          !Array.isArray(source[key])
        ) {
          target[key] = this.deepMerge(target[key] ?? {}, source[key]);
        } else {
          target[key] = source[key];
        }
      }
    }
    return target;
  }
}

@Injectable()
export class CustomMissingHandler implements TranslocoMissingHandler {
  private readonly injector = inject(Injector);

  translocoService: TranslocoService;

  handle(key: string, config: TranslocoConfig) {
    if (!this.translocoService) {
      this.translocoService = this.injector.get(TranslocoService);
    }

    const originalKey = key;
    while (key.includes('.')) {
      const split = key.split('.');
      split.length > 2 ? split.splice(1, 1) : split.shift(); // Remove the second or first segment
      key = split.join('.');
      const value = this.translocoService.translate(key);

      if (value !== key) {
        return value; // Return the translated value if found
      }
    }

    return originalKey; // Return the original key if no translation is found
  }
}
